<template>
    <Card class="flex flex-col text-sm tracking-wide">
        <div class="border-dark-lightest flex w-full items-center justify-between gap-2 border-b pb-2">
            <ProtocolStepHeading :index="index" />
            <div class="shrink-0">
                <slot v-if="$slots['icon-status']" name="icon-status" />
                <!-- A horse has started it: spelled out, a hover title never shows on a phone. -->
                <span v-else-if="locked" class="text-text-gray flex items-center gap-1 text-xs">
                    <LockClosedIcon class="h-4 w-4 shrink-0" />
                    {{ $t('protocol.step-locked') }}
                </span>
                <TrashIcon v-else-if="editable" class="text-brand-base h-5 w-5 cursor-pointer" @click="deleteStep" />
            </div>
        </div>

        <div class="mb-6 space-y-4 pt-3">
            <slot v-if="$slots.top" name="top" />
            <InputEditable
                v-else
                v-model="dayCountDraft"
                :placeholder="$t('metric.day-count')"
                :label="$t('metric.day-count')"
                type="number"
                :unit="$t('metric.day')"
                :editable="editable"
                single-click
                @input:leave="saveDayCount"
            >
                <div class="text-text-base flex items-center text-lg font-semibold">
                    <CalendarDaysIcon class="mr-1.5 h-5 w-5 shrink-0" />
                    {{ $t('protocol.x-days', toDayCount(dayCountDraft)) }}
                </div>
            </InputEditable>

            <InputEditable
                v-if="editable || step.description"
                v-model="step.description"
                :placeholder="$t('form.description')"
                type="textarea"
                :editable="editable"
                single-click
                @input:leave="({ valueChanged }) => valueChanged && saveStep({ description: step.description })"
            >
                <Paragraph v-if="step.description" class="text-wrap">{{ step.description }}</Paragraph>
                <Text v-else class="italic">{{ $t('form.add-description') }}</Text>
            </InputEditable>

            <Toggle v-if="editable" :model-value="restSwitch" @update:model-value="onRestSwitch">
                <span class="text-text-darker text-sm font-medium">{{ $t('protocol.rest-day-switch') }}</span>
            </Toggle>

            <div v-if="isRest" class="bg-dark-lighter flex items-center gap-2.5 rounded-lg p-3">
                <MoonIcon class="text-text-darker h-5 w-5 shrink-0" />
                <div class="min-w-0">
                    <div class="text-text-base text-sm font-semibold">{{ $t('protocol.rest-day-switch') }}</div>
                    <div class="text-text-gray text-xs">{{ $t('protocol.rest-day-help') }}</div>
                </div>
            </div>

            <template v-else>
                <div class="space-y-2">
                    <GaitBar :gaits="step.gaits" class="mb-3" />
                    <div v-for="(row, rowIndex) in step.gaits" :key="rowIndex" class="w-full">
                        <ProtocolStepGaitsEditable
                            v-model:duration="row.duration"
                            v-model:gait="row.gait"
                            :editable="editable"
                            @delete="() => removeRow(rowIndex, step)"
                            @save="() => saveGaitStep(rowIndex, row, step)"
                        />
                    </div>
                </div>
                <Button v-if="editable" small fit :action="() => addGait(step)">
                    {{ $t('program.add-gait') }}
                </Button>
            </template>
        </div>

        <!-- Turning the step into a rest day clears its gaits: said in the page, not by window.confirm. -->
        <Modal
            v-if="editable"
            v-model:is-open="restConfirmOpen"
            half-modal
            :title="$t('protocol.rest-day-confirm-title')"
        >
            <div class="space-y-4">
                <Paragraph>{{ $t('protocol.rest-day-confirm-message') }}</Paragraph>
                <Button :action="confirmRest">{{ $t('protocol.rest-day-confirm') }}</Button>
            </div>
        </Modal>
    </Card>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'

import { CalendarDaysIcon, MoonIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { LockClosedIcon } from '@heroicons/vue/24/solid'

import Card from '@/components/Card.vue'
import Text from '@/components/Text.vue'
import Paragraph from '@/components/Paragraph.vue'
import GaitBar from '@/components/GaitBar.vue'
import InputEditable from '@/components/form/InputEditable.vue'
import ProtocolStepGaitsEditable from '@/components/ProtocolStepGaitsEditable.vue'
import ProtocolStepHeading from '@/components/ProtocolStepHeading.vue'
import Button from '@/components/buttons/Primary.vue'
import Toggle from '@/components/buttons/Toggle.vue'
import Modal from '@/components/modals/Modal.vue'
import { toDayCount } from '@/lib/protocol-day-count'
import { isFlaggedRest, restSwitchNeedsConfirm, restSwitchPayload } from '@/lib/programs/rest-day'
import {
    STEP_DAY_COUNT_MAX,
    STEP_DAY_COUNT_MIN,
    STEP_SAVE_GAIT,
    STEP_SAVE_STEP,
    parseStepDayCount,
} from '@/lib/protocol-steps'

const props = defineProps({
    index: { type: Number, required: true },
    step: { type: Object, required: true },
    editable: { type: Boolean, default: true },
    // A horse has started the step (its `locked`): read-only for good.
    locked: { type: Boolean, default: false },
    startAt: { type: Object, default: null },
})

const emits = defineEmits(['click:delete', 'click:save', 'click:cancel'])

const { t } = useI18n()
const toast = useToast()

// The day count is edited on a draft, so a refused value (0 days, empty, too
// long) can be put back without ever reaching the API. It also resyncs when
// the step object is replaced: after a failed save the refetch hands back a new
// object holding the same old value, which a watch on the value alone misses.
const dayCountDraft = ref(props.step?.day_count)
watch([() => props.step, () => props.step?.day_count], ([step]) => {
    dayCountDraft.value = step?.day_count
})

// Opening the field with a single tap must not save (nor warn) when nothing was typed.
const saveDayCount = ({ valueChanged } = {}) => {
    if (!valueChanged) return

    const dayCount = parseStepDayCount(dayCountDraft.value)
    if (dayCount === null) {
        dayCountDraft.value = props.step?.day_count
        toast.add({
            severity: 'warn',
            summary: t('protocol.step-day-count-invalid', { min: STEP_DAY_COUNT_MIN, max: STEP_DAY_COUNT_MAX }),
            life: 4000,
        })
        return
    }

    dayCountDraft.value = dayCount
    if (dayCount === toDayCount(props.step?.day_count)) return
    emits('click:save', { day_count: dayCount }, STEP_SAVE_STEP)
}

// "Stall rest" (P7): said on the step (`is_rest`), never read from an empty step.
const isRest = computed(() => isFlaggedRest(props.step))

// The switch shows the rider's choice while it waits for the confirmation or the
// save, then follows the step again: a cancel, a refused save or the refetch
// that hands back a new step object all put it back on what the step says.
const restSwitch = ref(isRest.value)
const restConfirmOpen = ref(false)
watch([() => props.step, isRest], () => {
    restSwitch.value = isRest.value
})
// Closed without confirming (Cancel, swipe, Escape): the switch goes back.
// Confirmed, it stays on until the refetch says what the step now is.
let restConfirmed = false
watch(restConfirmOpen, (open) => {
    if (!open && !restConfirmed) restSwitch.value = isRest.value
    restConfirmed = false
})

const saveRest = (value) => {
    emits('click:save', restSwitchPayload(value), STEP_SAVE_STEP)
}

const onRestSwitch = (value) => {
    restSwitch.value = value
    if (value === isRest.value) return
    // Gaits would be lost: ask first. A step without any is switched at once.
    if (value && restSwitchNeedsConfirm(props.step)) {
        restConfirmOpen.value = true
        return
    }
    saveRest(value)
}

const confirmRest = () => {
    restConfirmed = true
    restConfirmOpen.value = false
    saveRest(true)
}

const deleteStep = () => {
    emits('click:delete', props.step.id)
}
// The second argument tells the page whether the step itself or one of its gait
// rows changed, so its confirmation names the right thing.
const saveStep = (step) => {
    emits('click:save', step, STEP_SAVE_STEP)
}
const saveGaitStep = (index, gait, step) => {
    step.gaits[index] = gait
    emits('click:save', step, STEP_SAVE_GAIT)
}
const addGait = async (step) => {
    if (!step.gaits) {
        step.gaits = []
    }
    step.gaits.push({ gait: 'walk', duration: 0 })
    emits('click:save', step, STEP_SAVE_GAIT)
}
const removeRow = async (rowIndex, step) => {
    step.gaits.splice(rowIndex, 1)
    emits('click:save', step, STEP_SAVE_GAIT)
}
</script>
